// src/pages/NotificationsPage/NotificationsPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./NotificationsPage.scss";
import { errorMessage } from "../../api/errorMessage";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import {
    emitLkfrNotificationsUpdated,
    fetchLkfrNotifications,
    markLkfrNotificationRead,
    NotificationInboxItem,
    NotificationInboxResponse,
} from "../../api/notificationInbox";

type NotificationItem = NotificationInboxItem;
type BackendResponse = NotificationInboxResponse;

type FilterType = "all" | "unread";

function fmtDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export const NotificationsPage: React.FC = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [items, setItems] = useState<NotificationItem[]>([]);
    const [filter, setFilter] = useState<FilterType>("all");

    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);

    const inFlight = useRef(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const markItemsAsRead = useCallback(async (itemsToMark: NotificationItem[]) => {
        const unreadItems = itemsToMark.filter((item) => !item.isRead);
        if (unreadItems.length === 0) {
            return;
        }

        const results = await Promise.all(unreadItems.map(async (item) => {
            try {
                const response = await markLkfrNotificationRead(item.id);
                return response?.ok ? String(item.id) : null;
            } catch { return null; }
        }));
        const readIds = new Set(results.filter((id): id is string => id !== null));
        if (!readIds.size) return;
        setItems((prev) => prev.map((item) => (
            readIds.has(String(item.id)) ? { ...item, isRead: true } : item
        )));
        emitLkfrNotificationsUpdated();
    }, []);

    const fetchPage = useCallback(async (cursor: string | null, first: boolean) => {
        if (inFlight.current) return;
        inFlight.current = true;

        if (first) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }

        try {
            const data = await fetchLkfrNotifications(20, cursor);

            if (!data || (data as any).ok !== true || !Array.isArray((data as any).items)) {
                throw new Error("Invalid backend response");
            }

            const resp = data as BackendResponse;

            setItems((prev) => {
                if (first) return resp.items;

                const existing = new Set(prev.map((x) => String(x.id)));
                const toAdd = resp.items.filter((x) => !existing.has(String(x.id)));
                return [...prev, ...toAdd];
            });

            setNextCursor(resp.nextCursor ?? null);
            setHasMore(Boolean(resp.nextCursor));
            await markItemsAsRead(resp.items);
        } catch (e: any) {
            setError(errorMessage(e, "Не удалось загрузить уведомления."));
            if (!first) setHasMore(false);
        } finally {
            if (first) setLoading(false);
            else setLoadingMore(false);
            inFlight.current = false;
        }
    }, [markItemsAsRead]);

    useEffect(() => {
        fetchPage(null, true);
    }, [fetchPage]);

    const filtered = useMemo(() => {
        return items.filter((n) => (filter === "all" ? true : !n.isRead));
    }, [items, filter]);

    const loadMore = useCallback(() => {
        if (!hasMore) return;
        if (!nextCursor) return;
        if (loading || loadingMore) return;
        fetchPage(nextCursor, false);
    }, [hasMore, nextCursor, loading, loadingMore, fetchPage]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { root: null, rootMargin: "200px", threshold: 0 }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, [loadMore]);

    const content = useMemo(() => {
        if (loading) return <div className="notif-empty">Загрузка…</div>;
        if (error) return <div className="notif-empty error" role="alert">{error}<button className="notif-loadmore" onClick={() => void fetchPage(null, true)}>Повторить</button></div>;
        if (filtered.length === 0 && !hasMore) return <div className="notif-empty">{filter === "unread" ? "Непрочитанных уведомлений нет" : "Уведомлений пока нет"}</div>;

        return (
            <>
                <div className="notif-list">
                    {filtered.map((n, index) => (<React.Fragment key={n.id}>{(index === 0 || filtered[index-1].createdAt.slice(0,10) !== n.createdAt.slice(0,10)) && <h2 className="notification-date-group">{new Date(n.createdAt).toLocaleDateString("ru-RU", { day:"numeric", month:"long", year:"numeric" })}</h2>}
                        <div key={n.id} className={`notif-card ${!n.isRead ? "unread" : ""}`}>
                            <div className="notif-top">
                                <div className="notif-title-row">
                                    {!n.isRead && <span className="notif-dot" />}
                                    <div className="notif-title">{n.title}</div>
                                </div>
                                <div className="notif-date">{fmtDate(n.createdAt)}</div>
                            </div>

                            {n.type && n.type !== "other" && (
                                <div className={`notif-tag notif-tag--${n.type}`}>
                                    {n.type === "promo" && "Акция"}
                                    {n.type === "system" && "Системное"}
                                    {n.type === "booking" && "Запись"}
                                </div>
                            )}

                            {n.text && <div className="notif-text">{n.text}</div>}
                        </div></React.Fragment>
                    ))}
                </div>

                {loadingMore && <div className="notif-empty">Загрузка…</div>}

                <div ref={sentinelRef} style={{ height: 1 }} />

                {!loadingMore && hasMore && nextCursor && (
                    <div className="notif-loadmore-wrap">
                        <button type="button" className="notif-loadmore" onClick={loadMore}>
                            Загрузить ещё
                        </button>
                    </div>
                )}
            </>
        );
    }, [loading, error, filtered, filter, hasMore, nextCursor, loadingMore, loadMore, fetchPage]);

    return (
        <div className="notif-page">
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />

            <div className="notif-container">
                <h1 className="notif-title-h1">Уведомления</h1>
                <div className="notif-filters">
                    <button
                        type="button"
                        className={`notif-filter-btn ${filter === "all" ? "active" : ""}`}
                        onClick={() => setFilter("all")}
                    >
                        Все
                    </button>
                    <button
                        type="button"
                        className={`notif-filter-btn ${filter === "unread" ? "active" : ""}`}
                        onClick={() => setFilter("unread")}
                    >
                        Непрочитанные
                    </button>
                </div>

                {content}
            </div>
        </div>
    );
};

export default NotificationsPage;
